import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  VStack,
  HStack,
  Heading,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  useToast,
  TableContainer,
  Select,
  Text,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, ViewIcon, RepeatIcon } from "@chakra-ui/icons";
import { adminApiService } from "../services/adminApiService";
import { Divisi, DivisiCreate, DivisiUpdate, Estate, QueryParams } from "../types";

const DivisiManagement: React.FC = () => {
  const [divisis, setDivisis] = useState<Divisi[]>([]);
  const [estates, setEstates] = useState<Estate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDivisi, setSelectedDivisi] = useState<Divisi | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState<DivisiCreate>({
    name: "",
    estate_id: 0,
    odoo_id: undefined,
    company_id: undefined,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const toast = useToast();

  // Fetch divisis and estates data
  const fetchDivisis = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: QueryParams = {
        search: searchTerm || undefined,
        include_disabled: includeDisabled,
      };
      const response = await adminApiService.getDivisis(params);
      setDivisis(response.data);
    } catch (error: any) {
      setError("Failed to fetch divisis");
      console.error("Error fetching divisis:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEstates = async () => {
    try {
      const response = await adminApiService.getEstates({ include_disabled: false });
      setEstates(response.data);
    } catch (error: any) {
      console.error("Error fetching estates:", error);
    }
  };

  useEffect(() => {
    fetchEstates();
  }, []);

  useEffect(() => {
    fetchDivisis();
  }, [searchTerm, includeDisabled]);

  const getEstateName = (estateId: number) => {
    const estate = estates.find(e => e.id === estateId);
    return estate ? estate.name : `Estate ID: ${estateId}`;
  };

  const handleOpenModal = (divisi?: Divisi) => {
    if (divisi) {
      setSelectedDivisi(divisi);
      setFormData({
        name: divisi.name,
        estate_id: divisi.estate_id,
        odoo_id: divisi.odoo_id,
        company_id: divisi.company_id,
      });
      setIsEditing(true);
    } else {
      setSelectedDivisi(null);
      setFormData({
        name: "",
        estate_id: 0,
        odoo_id: undefined,
        company_id: undefined,
      });
      setIsEditing(false);
    }
    onOpen();
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.estate_id) {
      toast({
        title: "Validation Error",
        description: "Name and Estate are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitLoading(true);
    try {
      if (isEditing && selectedDivisi) {
        const updateData: DivisiUpdate = {
          name: formData.name,
          estate_id: formData.estate_id,
          odoo_id: formData.odoo_id,
          company_id: formData.company_id,
        };
        await adminApiService.updateDivisi(selectedDivisi.id, updateData);
        toast({
          title: "Success",
          description: "Divisi updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        await adminApiService.createDivisi(formData);
        toast({
          title: "Success",
          description: "Divisi created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
      fetchDivisis();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? "update" : "create"} divisi: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error submitting divisi:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (divisi: Divisi) => {
    try {
      await adminApiService.deleteDivisi(divisi.id);
      toast({
        title: "Success",
        description: "Divisi deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchDivisis();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to delete divisi: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error deleting divisi:", error);
    }
  };

  const handleRestore = async (divisi: Divisi) => {
    try {
      await adminApiService.restoreDivisi(divisi.id);
      toast({
        title: "Success",
        description: "Divisi restored successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchDivisis();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to restore divisi: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error restoring divisi:", error);
    }
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Text>Loading divisis...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>Divisi Management</Heading>
          
          <HStack spacing={4} mb={4}>
            <Input
              placeholder="Search divisis..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              maxW="300px"
            />
            <Select
              value={includeDisabled ? "all" : "active"}
              onChange={(e) => setIncludeDisabled(e.target.value === "all")}
              maxW="200px"
            >
              <option value="active">Active Only</option>
              <option value="all">Include Disabled</option>
            </Select>
            <Button colorScheme="blue" onClick={() => handleOpenModal()}>
              Add Divisi
            </Button>
          </HStack>
        </Box>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Divisi Name</Th>
                <Th>Estate</Th>
                <Th>Odoo ID</Th>
                <Th>Company ID</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {divisis.map((divisi) => (
                <Tr key={divisi.id}>
                  <Td>{divisi.name}</Td>
                  <Td>
                    <Badge colorScheme="purple" variant="outline">
                      {getEstateName(divisi.estate_id)}
                    </Badge>
                  </Td>
                  <Td>
                    {divisi.odoo_id ? (
                      <Badge colorScheme="blue" variant="outline">
                        {divisi.odoo_id}
                      </Badge>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )}
                  </Td>
                  <Td>
                    {divisi.company_id ? (
                      <Badge colorScheme="green" variant="outline">
                        {divisi.company_id}
                      </Badge>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={divisi.is_disabled ? "red" : "green"}>
                      {divisi.is_disabled ? "Disabled" : "Active"}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="View"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(divisi)}
                      />
                      {!divisi.is_disabled ? (
                        <>
                          <IconButton
                            aria-label="Edit"
                            icon={<EditIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => handleOpenModal(divisi)}
                          />
                          <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => handleDelete(divisi)}
                          />
                        </>
                      ) : (
                        <IconButton
                          aria-label="Restore"
                          icon={<RepeatIcon />}
                          size="sm"
                          colorScheme="green"
                          variant="outline"
                          onClick={() => handleRestore(divisi)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {divisis.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            No divisis found matching your criteria.
          </Alert>
        )}
      </VStack>

      {/* Divisi Form Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? "Edit Divisi" : "Add New Divisi"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Divisi Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter divisi name"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Estate</FormLabel>
                <Select
                  value={formData.estate_id}
                  onChange={(e) => setFormData({...formData, estate_id: parseInt(e.target.value)})}
                  placeholder="Select estate"
                >
                  <option value={0} disabled>Select an estate</option>
                  {estates.map((estate) => (
                    <option key={estate.id} value={estate.id}>
                      {estate.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Odoo ID</FormLabel>
                <Input
                  type="number"
                  value={formData.odoo_id || ""}
                  onChange={(e) => setFormData({...formData, odoo_id: e.target.value ? parseInt(e.target.value) : undefined})}
                  placeholder="Enter Odoo ID (optional)"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Company ID</FormLabel>
                <Input
                  type="number"
                  value={formData.company_id || ""}
                  onChange={(e) => setFormData({...formData, company_id: e.target.value ? parseInt(e.target.value) : undefined})}
                  placeholder="Enter Company ID (optional)"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmit}
              isLoading={submitLoading}
              loadingText={isEditing ? "Updating..." : "Creating..."}
            >
              {isEditing ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DivisiManagement;
